import { nextSetupRevisionStatus, type SetupRevisionStatus } from './revisionState';

/**
 * The last checkpoint of a setup revision: the mapping readiness verdict for the
 * setup whose profile was already saved and read back. Kept free of any mapping
 * feature type so the shared layer never depends on a feature.
 */

export const SetupReadinessVerdicts = ['pending', 'verified', 'failed'] as const;

export type SetupReadinessVerdict = (typeof SetupReadinessVerdicts)[number];

/** What the caller should do with a verdict: nothing, settle, or report failure. */
export type SetupReadinessAction = 'wait' | 'confirm' | 'reportFailure';

/**
 * Resolves a readiness verdict against the revision it claims to speak for. A
 * verdict only counts while a revision is actually waiting for one: readiness is
 * measured continuously, so a stale or unrelated verdict must never settle a
 * revision that has not recorded a verified read-back.
 */
export const resolveSetupReadinessAction = (
  status: SetupRevisionStatus,
  verdict: SetupReadinessVerdict
): SetupReadinessAction => {
  if (nextSetupRevisionStatus(status, 'readinessVerified') === null) {
    return 'wait';
  }

  if (verdict === 'verified') {
    return 'confirm';
  }

  if (verdict === 'failed') {
    return 'reportFailure';
  }

  return 'wait';
};
